import { AdminForm, useCreateAdmin, useGetAdmins } from "@entities/admin";
import { useAuth } from "@entities/auth";
import { PermissionGroups } from "./Permission";
import { ACCOUNT_STATUS, AdminUsers, CreateAdmin } from "@shared/lib/types";
import {
  Avatar,
  Button,
  Modal,
  Row,
  Select,
  Table,
  TableTitleLayout,
  Tag
} from "@shared/ui";
import { useEffect, useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { useGetRoles } from "@entities/admin/lib/hooks/useGetRoles";
import { useGetPermissionGroups } from "@entities/permissionGroups/lib/hooks";
import { ManageEmployee } from "@features/ManageEmpoyeeDrawer/ManageEmployee";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Select as AntdSelect, Col, Drawer, Flex, Input } from "antd";
import { ColumnsType } from "antd/es/table";
import { Search } from "lucide-react";
import styles from "./styles.module.scss";

const { Option } = AntdSelect;

export function AdminManagement() {
  const { beUser } = useAuth();
  const isSuperAdmin = beUser?.permissions?.includes("super");
  const isPermissionGroupsEnabled = beUser?.permissions?.includes("view-permission-module");

  const [openPermissionsDrawer, setOpenPermissionsDrawer] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [accountStatus, setAccountStatus] = useState<ACCOUNT_STATUS[]>([
    ACCOUNT_STATUS.ACTIVE,
  ]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [selectedPermissionGroups, setSelectedPermissionGroups] = useState<
    string[]
  >([]);
  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  const handleStatusChange = (value: ACCOUNT_STATUS[] | undefined) => {
    if (!value) return setAccountStatus([]);
    setAccountStatus(value);
  };
  const handleRoleChange = (value: string[] | undefined) => {
    if (!value) return setSelectedRoles([]);
    setSelectedRoles(value);
  };

  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const { admins, isFetching } = useGetAdmins(
    pageSize,
    paegNo,
    selectedRoles,
    searchQuery,
    accountStatus,
    selectedPermissions,
    selectedPermissionGroups
  );

  const { roles } = useGetRoles();
  const { create, isCreating, isCreated } = useCreateAdmin();

  const { permissionGroups, isFetching: isPermissionGroupsFetching } =
    useGetPermissionGroups();
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<string>("");

  const handlePermissionChange = (value: string[] | undefined) => {
    if (!value) return setSelectedPermissions([]);
    setSelectedPermissions(value);
  };

  const rolesToDisplay = Object.keys(roles || {});

  const handleCreateAdmin = () => {
    setSelectedAdmin("");
    setModalVisible(true);
  };

  const handleSubmit = (adminData: CreateAdmin) => {
    create(adminData);
    // setModalVisible(false);
  };
  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };
  useEffect(() => {
    setPageNo(1);
  }, [
    pageSize,
    selectedRoles,
    searchQuery,
    accountStatus,
    selectedPermissionGroups,
  ]);

  const columns: ColumnsType<AdminUsers> = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "name",
      render: (_: string, record: AdminUsers) => (
        <Row
          align="middle"
          gutter={[8, 8]}
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedAdmin(record.adminId);
          }}
        >
          <Col>
            <Avatar
              src={record.profilePicUrl}
              alt="Profile Picture"
              size={40}
            />
          </Col>
          <Col>
            {record.firstName} {record.lastName}
          </Col>
        </Row>
      ),
    },

    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      filterCount: selectedRoles.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Role"
          placeholder="Select Roles"
          itemsToDisplay={rolesToDisplay}
          selectedItems={selectedRoles}
          handleSelectionChange={(values) => handleRoleChange(values)}
          props={props}
        />
      ),
    },

    {
      title: "Permission Groups",
      dataIndex: "permissionGroups",
      key: "permissionGroups",
      filterCount: selectedPermissionGroups.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      render: (_: string, record: AdminUsers) => (
        <TextWithTooltip
          text={
            record?.permissionGroups?.map((group) => group.name).join(", ") ||
            "-"
          }
          limit={30}
        />
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        return (
          <SelectFilter
            title="Permission Groups"
            itemsToDisplay={(permissionGroups || [])?.map((group) => ({
              value: group._id,
              name: group.name,
            }))}
            isOptionsLoading={isPermissionGroupsFetching}
            selectedItems={selectedPermissionGroups}
            handleSelectionChange={(value) =>
              setSelectedPermissionGroups(value)
            }
            props={props}
          />
        );
      },
    },
    { title: "Email-Id", dataIndex: "emailId", key: "email" },
    {
      title: "Phone Number",
      render: (_: string, record: AdminUsers) =>
        `${record.countryCode} ${record.phoneNumber}`,
    },
    {
      title: "Status",
      dataIndex: "accountStatus",
      key: "status",
      filterCount: accountStatus.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Status"
          placeholder="Select status"
          itemsToDisplay={Object.values(ACCOUNT_STATUS)}
          selectedItems={accountStatus}
          handleSelectionChange={(items) =>
            handleStatusChange(items as ACCOUNT_STATUS[])
          }
          props={props}
        />
      ),
      render: (status: ACCOUNT_STATUS) => (
        <Tag color={status === ACCOUNT_STATUS.ACTIVE ? "green" : "red"}>
          {status}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    if (isCreated) {
      setModalVisible(false);
    }
  }, [isCreated]);

  return (
    <div>
      <h1 className={styles.title}>Employee</h1>


        <Table
          dataSource={admins?.data}
          columns={columns}
          loading={isFetching}
          title={() => <TableTitleLayout 
            filterComponent={<Flex justify="space-between" style={{width: "100%"}}>
              <Input
            placeholder="Search By name or phone number"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: 350 }}
            prefix={<Search size={14}/>}
          />
          <Flex gap={8}>
            {(isSuperAdmin || isPermissionGroupsEnabled) && (
              <Button onClick={() => setOpenPermissionsDrawer(true)}>
                Manage Permission
              </Button>
            )}
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => handleCreateAdmin()}
            >
              Create Employee
            </Button>
          </Flex>
            </Flex>}
            totalText={`${admins?.pagination?.totalCount || 0} Records found`}
          />}
          rowKey="_id"
          pagination={{
            current: paegNo,
            pageSize: pageSize,
            total: admins?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
        />
      <Modal
        title={"Create Employee"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <AdminForm
          onSubmit={handleSubmit}
          isSubmitting={isCreating}
          roles={rolesToDisplay}
        />
      </Modal>
      <Drawer
        destroyOnClose
        title={"Employee"}
        open={Boolean(selectedAdmin)}
        onClose={() => {
          setSelectedAdmin("");
        }}
        footer={null}
        width={600}
      >
        {selectedAdmin && (
          <ManageEmployee
            employeeId={selectedAdmin}
            handleClose={() => setSelectedAdmin("")}
          />
        )}
      </Drawer>
      <Drawer 
        open={openPermissionsDrawer}
        onClose={() => setOpenPermissionsDrawer(false)}
        title="Manage Permission Groups"
        destroyOnClose
        width={600}
      >
        <PermissionGroups />
      </Drawer>
    </div>
  );
}
